import {
  getPromotions,
  normalizePromotionsPayload,
  reconcilePromotionImagesById,
  savePromotions,
} from "../local/promotions";
import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";

const PROMOTIONS_PATCH_FILE = "promotions_state_patch.sql";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const isNetworkFetchError = (error) => {
  const message = String(error?.message || "").toLowerCase();

  return (
    error instanceof TypeError ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed")
  );
};

const buildNetworkFetchError = (fallbackMessage) =>
  new Error(
    fallbackMessage ||
      "No pudimos conectar con Supabase en este momento. Seguimos con la ultima version local disponible."
  );

const isMissingRelationError = (error, relationName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeRelationName = String(relationName || "").toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(safeRelationName) ||
    message.includes(`relation "public.${safeRelationName}" does not exist`)
  );
};

const isMissingFunctionError = (error, functionName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeFunctionName = String(functionName || "").toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes(safeFunctionName) ||
    message.includes("could not find the function")
  );
};

const ensureBranchId = (branchId) => {
  const safeBranchId = String(branchId || "").trim();

  if (!safeBranchId) {
    throw new Error("No encontramos una sucursal activa para cargar las promos.");
  }

  return safeBranchId;
};

const buildMissingPatchError = () =>
  new Error(
    `Falta aplicar ${PROMOTIONS_PATCH_FILE} en Supabase para guardar las promos y combos de forma remota.`
  );

export const getBranchPromotions = async (branchId) => {
  ensureSupabaseReady();
  const safeBranchId = ensureBranchId(branchId);
  let response;

  try {
    response = await supabase
      .from("branch_promotions_state")
      .select("payload")
      .eq("branch_id", safeBranchId)
      .maybeSingle();
  } catch (error) {
    if (isNetworkFetchError(error)) {
      throw buildNetworkFetchError();
    }

    throw normalizeError(error, "No pudimos cargar las promos de la sucursal.");
  }

  const { data, error } = response;

  if (error) {
    if (isMissingRelationError(error, "branch_promotions_state")) {
      throw buildMissingPatchError();
    }

    throw normalizeError(error, "No pudimos cargar las promos de la sucursal.");
  }

  return normalizePromotionsPayload(data?.payload);
};

export const saveBranchPromotions = async (branchId, promotions) => {
  ensureSupabaseReady();
  const safeBranchId = ensureBranchId(branchId);
  const payload = normalizePromotionsPayload(promotions);
  let response;

  try {
    response = await supabase
      .from("branch_promotions_state")
      .upsert(
        {
          branch_id: safeBranchId,
          payload,
        },
        {
          onConflict: "branch_id",
        }
      )
      .select("payload")
      .single();
  } catch (error) {
    if (isNetworkFetchError(error)) {
      throw buildNetworkFetchError(
        "No pudimos guardar las promos en Supabase por un problema de conexion."
      );
    }

    throw normalizeError(error, "No pudimos guardar las promos de la sucursal.");
  }

  const { data, error } = response;

  if (error) {
    if (isMissingRelationError(error, "branch_promotions_state")) {
      throw buildMissingPatchError();
    }

    throw normalizeError(error, "No pudimos guardar las promos de la sucursal.");
  }

  return normalizePromotionsPayload(data?.payload);
};

export const getPublicBranchPromotions = async (branchId) => {
  ensureSupabaseReady();
  const safeBranchId = ensureBranchId(branchId);
  let response;

  try {
    response = await supabase.rpc("get_public_promotions_by_branch_id", {
      p_branch_id: safeBranchId,
    });
  } catch (error) {
    if (isNetworkFetchError(error)) {
      throw buildNetworkFetchError();
    }

    throw normalizeError(error, "No pudimos cargar las promos publicas de la sucursal.");
  }

  const { data, error } = response;

  if (error) {
    if (isMissingFunctionError(error, "get_public_promotions_by_branch_id")) {
      throw buildMissingPatchError();
    }

    throw normalizeError(error, "No pudimos cargar las promos publicas de la sucursal.");
  }

  return normalizePromotionsPayload(data);
};

export const hydrateBranchPromotionsToLocal = async (
  restaurant,
  branchId,
  options = {}
) => {
  const promotions = options.publicRead
    ? await getPublicBranchPromotions(branchId)
    : await getBranchPromotions(branchId);
  const localPromotions = getPromotions(restaurant || { id: branchId });
  const reconciled = reconcilePromotionImagesById(localPromotions, promotions);
  let finalPromotions = reconciled.records;

  if (!options.publicRead && reconciled.recovered > 0) {
    try {
      finalPromotions = await saveBranchPromotions(branchId, reconciled.records);
    } catch (error) {
      finalPromotions = reconciled.records;
    }
  }

  try {
    savePromotions(restaurant || { id: branchId }, finalPromotions, {
      compact: options.compact === true,
    });
  } catch (error) {
    // El cache local no puede tirar abajo la fuente remota.
  }

  return finalPromotions;
};
